import logger from "../../utils/logger";
import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.\n' +
    'Required variables:\n' +
    '  - VITE_SUPABASE_URL\n' +
    '  - VITE_SUPABASE_ANON_KEY'
  );
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'sb-propertycheckpro-auth-token',
    // Add retry settings for token refresh
    retryAttempts: 3,
    retryDelay: 500,
  },
  global: {
    headers: {
      'x-client-info': 'propertycheckpro',
    },
    // Use native fetch - let Supabase handle AbortController properly
  },
  db: {
    schema: 'public',
  },
  realtime: {
    timeout: 30000,
  },
});

// Expose supabase to window for debugging (dev only)
if (import.meta.env.DEV) {
  window.supabase = supabase;
  logger.log('[Supabase] Client exposed to window.supabase for debugging');
}

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper function to ensure session is valid before making requests
export const ensureValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('[ensureValidSession] Error getting session:', error);
      return false;
    }

    if (!session) {
      console.warn('[ensureValidSession] No session found');
      return false;
    }

    // Check if token expires in the next 60 seconds
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiresAt - now;

    if (timeUntilExpiry < 60) {
      logger.log('[ensureValidSession] Token expires soon, refreshing...');
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        console.error('[ensureValidSession] Error refreshing session:', refreshError);
        return false;
      }

      logger.log('[ensureValidSession] Session refreshed successfully');
      return !!refreshedSession;
    }

    return true;
  } catch (err) {
    console.error('[ensureValidSession] Unexpected error:', err);
    return false;
  }
};

// Helper function to get user profile with retry logic
export const getUserProfile = async (userId, retries = 3) => {
  // Ensure session is valid before fetching profile
  await ensureValidSession();

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      logger.log(`[getUserProfile] Fetching profile for user ${userId} (attempt ${attempt}/${retries})`);

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[getUserProfile] Supabase error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          status: error.status,
        });

        // If it's a 406 or network error, retry
        if (attempt < retries && (error.code === '406' || error.message?.includes('fetch'))) {
          logger.log(`[getUserProfile] Retrying after ${attempt * 500}ms...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 500));
          continue;
        }

        throw error;
      }

      // Profile doesn't exist yet — create a default one
      if (!data) {
        console.warn(`[getUserProfile] No profile found for ${userId}, creating default profile`);
        const { data: { user } } = await supabase.auth.getUser();
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            email: user?.email || '',
            full_name: user?.user_metadata?.full_name || '',
            subscription_plan: 'free',
            subscription_status: 'active',
            properties_count: 0,
            attribution: user?.user_metadata?.attribution || null,
          }, { onConflict: 'id', ignoreDuplicates: false })
          .select()
          .single();

        if (insertError) {
          console.error('[getUserProfile] Error creating default profile:', insertError);
          // Return a minimal in-memory profile so the app doesn't crash
          return {
            id: userId,
            email: user?.email || '',
            full_name: user?.user_metadata?.full_name || '',
            subscription_plan: 'free',
            subscription_status: 'active',
            properties_count: 0,
          };
        }

        logger.log('[getUserProfile] Default profile created successfully');
        return newProfile;
      }

      logger.log('[getUserProfile] Profile fetched successfully');
      return data;
    } catch (err) {
      if (attempt === retries) {
        console.error('[getUserProfile] All retry attempts failed:', err);
        throw err;
      }
    }
  }
};
